import React, { useMemo, useEffect } from 'react'
import messages from './messages'
import { useIntl } from 'react-intl'
import { Person, Chip, Icon, Avatar } from '@arnsbogroup/react-voicearchive'
import { useParams } from 'react-router-dom'
import useApi from 'hooks/useApi'
import Chat from 'components/Chat'
import classNames from 'classnames'

export default ({ task }) => {
  const { formatDate, formatTime } = useIntl()
  const [events, loading,, refetch] = useApi(`/rest/v2/task/${task.uid}/events`, { load: false })

  useEffect(() => {
    if (!!task?.uid) {
      refetch(`/rest/v2/task/${task.uid}/events`)
    }
  }, [task?.uid, task?.tstamp])

  const timeline = useMemo(() => {
    if (loading) {
      return [{
        id: 'loading',
        icon: ['fal', 'hourglass'],
        color: 'gray-400',
        name: 'Fetching task events...',
      }]
    }

    return [
      ...(events || []).map((event) => ({
        ...event,
        icon: 'thumbs-up',
        date: formatDate(event.timestamp * 1000) + ' ' + formatTime(event.timestamp * 1000),
        user: event?.user || null,
        backgroundColor: {
          red: 'bg-negative',
          green: 'bg-positive',
          yellow: 'bg-warning',
          grey: 'bg-gray-400',
        }[event?.color || 'bg-grey'],
        color: {
          red: 'negative',
          green: 'positive',
          yellow: 'warning',
          grey: 'gray-400',
        }[event?.color || 'grey'],
        textColor: {
          red: 'text-negative',
          green: 'text-positive',
          yellow: 'text-warning',
          grey: 'text-gray-400',
        }[event?.color || 'text-grey']
      })),
      ...(task.isInvoiced > 0 ? [{
        id: 'invoicing',
        icon: ['fal', 'check'],
        name: `Invoiced #${task.isInvoiced} issued`,
        date: null,
        datetime: null,
        color: 'transparent',
        textColor: 'text-gray-400',
        backgroundColor: 'bg-gray-400',
      }] : []),
    ].sort((a, b) => a.timestamp < b.timestamp ? 1 : -1)
  }, [events, task, loading])

  return (
    <ul role="list" className="-mb-6 p-2">
      {timeline.map((item, itemIdx) => (
        <li key={item.id}>
          <div className="relative pb-3 2xl:pb-6">
            {itemIdx !== timeline.length - 1 ? (
              <span
                className="absolute top-3 left-3 -ml-px h-full w-0.5 bg-gray-200"
                aria-hidden="true"
              />
            ) : null}
            <div className="relative flex space-x-3">
              <div>
                { item.user ? (
                  <Avatar
                    size="xs"
                    src={item.user.image}
                    title={item.user.name}
                    initials={item.user?.name.match(/(\b\S)?/g).join("").match(/(^\S|\S$)?/g).join("").toUpperCase()}
                  />
                ) : (
                  <span
                    className={classNames(
                      item.backgroundColor,
                      'h-6 w-6 rounded-full flex items-center justify-center'
                    )}
                  >
                    <Icon icon={item.icon} className="text-xs text-white" aria-hidden="true" />
                  </span>
                ) }
              </div>
              <div className={classNames('2xl:flex 2xl:space-x-4 min-w-0 flex-1 justify-between pt-1.5 text-sm', {
                'text-gray-500': itemIdx > 0 || loading,
                [`${item.textColor} font-medium`]: itemIdx === 0 && !loading,
              })}>
                <p>
                  { item.name }
                </p>

                <div className="whitespace-nowrap text-xs 2xl:text-sm 2xl:text-right">
                  { item?.date &&
                    <time dateTime={item.datetime} >
                      {item.date}
                    </time>
                  }

                  <Icon
                    icon={{
                      negative: ['fal', 'triangle-exclamation'],
                      positive: ['fal', 'circle-check'],
                      warning: ['fal', 'circle-info'],
                      grey: ['fal', 'circle'],
                    }[item?.color || 'grey']}
                    className={`${item.textColor} ml-2`}
                  />
                </div>
              </div>
            </div>
          </div>
        </li>
      ))}
    </ul>
  )
}
